import { useSEO } from "@/hooks/useSEO";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, BookOpen, Calendar, ChevronRight, Search } from "lucide-react";
import { Section, Badge, AnimatedCard } from "@/components/ui/shared";
import { SkeletonCardGrid } from "@/components/ui/Skeleton";
import { EmptyState, ErrorState, Pagination } from "@/components/ui/States";
import { useDebounce } from "@/hooks/useDebounce";
import { useSeerahEvents, useSeerahFilters, usePageView } from "@/lib/api";
import type { SeerahEventSummary, SeerahEra } from "@/lib/api";
import { ERA_LABELS } from "@/lib/constants";

const PAGE_SIZE = 24;

/* ============================================
   Event Card
   ============================================ */

function EventCard({ event, index }: { event: SeerahEventSummary; index: number }) {
  return (
    <AnimatedCard delay={index * 0.06}>
      <Link to={`/seerah/${event.id}`} className="block card-elevated p-6 group">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex flex-wrap gap-2">
            <Badge value={event.era} />
            <Badge value={event.category} />
          </div>
          {event.dateGregorian && (
            <span className="text-xs text-[var(--color-ink-muted)] whitespace-nowrap flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {event.dateGregorian}
            </span>
          )}
        </div>

        <h3 className="font-[var(--font-display)] text-lg font-semibold text-[var(--color-midnight)] group-hover:text-[var(--color-emerald)] transition-colors mb-1">
          {event.title}
        </h3>

        {event.titleArabic && (
          <p className="font-arabic text-base text-[var(--color-gold-dim)]/70 mb-3">
            {event.titleArabic}
          </p>
        )}

        {event.significance && (
          <p className="text-sm text-[var(--color-ink-muted)] leading-relaxed line-clamp-2 mb-3">
            {event.significance}
          </p>
        )}

        <div className="flex items-center justify-between">
          {event.location && (
            <span className="text-xs text-[var(--color-ink-muted)] flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {event.location}
            </span>
          )}
          <ChevronRight className="w-4 h-4 text-[var(--color-gold)] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
        </div>
      </Link>
    </AnimatedCard>
  );
}

/* ============================================
   Seerah Explorer Page
   ============================================ */

export default function SeerahPage() {
  useSEO("Seerah Explorer", "The life of Prophet Muhammad ﷺ from Makkah to Madinah: an interactive Seerah timeline with every event sourced from the Quran, authentic hadith and classical biographies.");
  const [activeEra, setActiveEra] = useState<SeerahEra | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  const search = useDebounce(searchQuery.trim());

  const { data: eventsResponse, isLoading, isError, error, refetch } = useSeerahEvents({
    era: activeEra === "all" ? undefined : activeEra,
    search: search || undefined,
    page,
    page_size: PAGE_SIZE,
  });

  const { data: filters } = useSeerahFilters();

  usePageView("seerah");

  const events: SeerahEventSummary[] = eventsResponse?.events ?? [];
  const total = eventsResponse?.total ?? 0;

  const eraPills: { key: string; label: string; count?: number }[] = [
    { key: "all", label: "All Eras" },
    ...(filters?.eras?.map((e) => ({
      key: e.era,
      label: ERA_LABELS[e.era] || e.era,
      count: e.count,
    })) ?? Object.entries(ERA_LABELS).map(([key, label]) => ({ key, label }))),
  ];

  return (
    <main className="pt-16">
      {/* Hero */}
      <div className="bg-[var(--color-midnight)] py-16 md:py-24 pattern-islamic relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-midnight)] via-transparent to-[var(--color-midnight)]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[var(--color-gold)] text-sm font-semibold uppercase tracking-[0.2em]">The Prophetic Biography</span>
            <h1 className="mt-3 font-[var(--font-display)] text-4xl md:text-5xl font-bold text-[var(--color-cream)]">Seerah Explorer</h1>
            <p className="mt-4 text-[var(--color-cream)]/60 max-w-2xl mx-auto">
              Walk through the life of Prophet Muhammad ﷺ — every event authenticated with Quran and Sahih hadith references.
            </p>
          </motion.div>
        </div>
      </div>

      <Section>
        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-ink-muted)]" />
            <input type="search" aria-label="Search Seerah events" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder="Search events, locations, descriptions..."
              className="w-full pl-11 pr-4 py-3 rounded-full border border-[var(--color-cream-dark)] bg-white text-sm focus:outline-none focus:border-[var(--color-gold)] focus:ring-2 focus:ring-[var(--color-gold)]/20 transition-all" />
          </div>
        </div>

        {/* Era Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {eraPills.map((era) => (
            <button key={era.key} onClick={() => { setActiveEra(era.key as SeerahEra | "all"); setPage(1); }}
              className={`pill ${activeEra === era.key ? "active" : ""}`}>
              {era.label}
              {era.count !== undefined && <span className="ml-1 text-[10px] opacity-60">({era.count})</span>}
            </button>
          ))}
        </div>

        {/* Content — three states */}
        {isLoading ? (
          <SkeletonCardGrid count={6} />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <>
            <div className="text-center text-sm text-[var(--color-ink-muted)] mb-8">
              Showing {events.length} of {total} events
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((event, i) => (
                <EventCard key={event.id} event={event} index={i} />
              ))}
            </div>

            {events.length === 0 && (
              <EmptyState icon={<BookOpen className="w-12 h-12" />}>No events match your search.</EmptyState>
            )}

            <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
          </>
        )}
      </Section>
    </main>
  );
}
